import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import HighlightedText from "components/Shared/HighlightedText";
import { useAuth, useAppMeta, useTablePagination } from "hooks";
import { useCustomParams } from "hooks/useCustomParams";
import { useLazyListShipmentChargeReportQuery } from "store/services/reportApi";
import ShipmentChargeFilter from "./ShipmentChargeFilter";

const ShipmentChargeReport = () => {
  const { viewEntity } = useAuth();
  const [searchParams] = useSearchParams();
  const { params } = useCustomParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [fetchReport, { data, isLoading, isFetching, error }] = useLazyListShipmentChargeReportQuery();
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Reports", path: "/app/crm/reports/list" }, { title: "Shipment Charge Report" }]
  useEffect(() => {
    // Skip firing until ShipmentChargeFilter has set the default date range.
    // Prevents a duplicate request: one with empty dates, one with the defaults.
    if (!params.timeRange) return;

    fetchReport({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      page: currentPage,
      limit: perPage,
      keyword,
      vendorId: params.vendorId || "",
      customerId: params.customerId || "",
      fromDate: params.fromDate || "",
      toDate: params.toDate || "",
      sortBy: (params.sort || "totalCharge:-1").split(":")[0],
      sortDirection: Number((params.sort || "totalCharge:-1").split(":")[1]),
    });
  }, [
    fetchReport,
    viewEntity,
    currentPage,
    perPage,
    keyword,
    params.timeRange,
    params.vendorId,
    params.customerId,
    params.fromDate,
    params.toDate,
    params.sort,
  ]);
  const columns = [
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentId,
      cell: (row) => (
        <Link to={`/app/crm/shipment/${row._id}/detail`}>
          <HighlightedText text={row.shipmentId || "N/A"} highlight={keyword} />
        </Link>
      ),
    },
    {
      name: "Vendor / Customer",
      selector: (row) => row.customerId?.customerUser?.name || row.customerId?.name || row.vendor?.shopName || "N/A",
      cell: (row) => {
        const customer = row.customerId?.customerUser?.name || row.customerId?.name;
        const vendor = row.vendor?.shopName;
        return customer ? `CUS: ${customer}` : vendor ? `VEN: ${vendor}` : "N/A";
      },
    },
    {
      name: "Total Charge",
      selector: (row) => row.totalCharge,
      cell: (row) => `Rs ${(row.totalCharge || 0).toFixed(2)}`,
    },
    {
      name: "Created At",
      selector: (row) => row.createdAt,
      cell: (row) => <AppTableDateTime date={row.createdAt} />,
    },
    {
      name: "Shipment Status",
      selector: (row) => row.status,
      cell: (row) => `${row.status}`,
    },
  ];

  useAppMeta("Shipment Charge Report");

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <ShipmentChargeFilter disabled={isLoading || isFetching} />
        <Box sx={{ px: 2, pb: 1, textAlign: "right" }}>
          <Typography variant="subtitle2">Page Total: Rs {(data?.data || []).reduce((sum, row) => sum + (row.totalCharge || 0), 0).toFixed(2)}</Typography>
        </Box>
        <AppTable
          columns={columns}
          data={data?.data || []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          paginationTotalRows={data?.totalItems || 0}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          noDataComponent={<Box sx={{ p: 4 }}><Typography>No active shipment charges found</Typography></Box>}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default ShipmentChargeReport
